import styles from "./MessageList.module.css";
import type { ChatMessage, ChatMessageUser, ChatMessageBot } from "../../types/chat.types";
import ChatMessageComponent from "../ChatMessage/ChatMessage";
import TypingDots from "../TypingDots/TypingDots";
import IntroMessage from "../IntroMessage/IntroMessage";
import React, { useEffect, useRef } from "react";
import FeedbackQuestion from "../FeedbackQuestion/FeedbackQuestion";
import GreetingMessage from "../GreetingMessages/GreetingMessages";

interface MessageListProps {
  messages: ChatMessage[];
  showTypingDots: boolean;
  introMessageText: string;
  greetingMessages: string[];
  botSenderName?: string;
  onSelectQuickReply?: (value: string) => void;
}

const MessageList = ({ messages, showTypingDots, introMessageText, greetingMessages, botSenderName, onSelectQuickReply }: MessageListProps) => {
  
  const isBotMessage = (msg: ChatMessage): msg is ChatMessageBot => {
    return msg.type === "bot";
  };

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const messageListElement = scrollContainerRef.current;

    if (!messageListElement) return;

    messageListElement.scrollTo({ top: messageListElement.scrollHeight, behavior: "smooth" });
  }, [messages, showTypingDots]);

  return (
    <section 
      ref={scrollContainerRef} 
      className={styles.messageList}
      role="log"
      aria-live="polite"
      aria-atomic="false"
    >
      <IntroMessage introMessage={introMessageText} />
      {greetingMessages && greetingMessages.length > 0 && (
        <GreetingMessage greetingMessages={greetingMessages} botSenderName={botSenderName} />
      )}
      {messages
      .filter(msg => 
        !(msg.type === "user" && (msg as ChatMessageUser).autoPost) &&
        !(msg.type === "bot" && (msg as ChatMessageBot).handover)
      )
      .map((msg, index) => {
        return (
          <React.Fragment key={index}>
            <ChatMessageComponent
              sender={msg.sender}
              content={msg.content}
              type={msg.type}
              citations={isBotMessage(msg) ? msg.citations : undefined}
              linkButtonItems={isBotMessage(msg) ? msg.linkButtonItems : []}
              quickReplies={isBotMessage(msg) ? msg.quickReplies : undefined}
              disclaimerText={isBotMessage(msg) ? msg.disclaimerText : undefined}
              timestamp={msg.type === "agent" ? (msg as any).timestamp : undefined}
              agentName={msg.type === "agent" ? msg.sender : undefined}
              onSelectQuickReply={onSelectQuickReply}
            />
            {isBotMessage(msg) && "askForFeedback" in msg && msg.askForFeedback && (
              <FeedbackQuestion
                messageId={msg.messageId}
                sessionId={msg.sessionId}
                agent={msg.agent}
              />
            )}
          </React.Fragment>
        );
      })}
      {showTypingDots && <TypingDots botSenderName={botSenderName} />}
    </section>
  );
};

export default MessageList;
